import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { claimsAPI } from '../services/api';

const ClaimDetail = () => {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const loadClaim = async () => {
    try {
      const response = await claimsAPI.getById(reference);
      setClaim(response.data);
    } catch (error) {
      console.error('Error loading claim:', error);
      setError('Failed to load claim details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading claim details...</p>
        </div>
      </Layout>
    );
  }

  if (error || !claim) {
    return (
      <Layout>
        <div style={{ padding: '1rem', background: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
          <p>{error || 'Claim not found'}</p>
          <button
            onClick={() => navigate('/claims')}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#c62828',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Claims
          </button>
        </div>
      </Layout>
    );
  }

  const getStatusColor = (isActive) => isActive ? '#4A90E2' : '#999';

  const isValidDate = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return date.getFullYear() >= 1971;
  };

  return (
    <Layout>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '2rem', fontSize: '0.9rem', color: '#666' }}>
        <Link to="/claims" style={{ color: '#4A90E2', textDecoration: 'none' }}>
          My Claims
        </Link>
        {' '}&gt;{' '}
        <span>{claim.claim_reference}</span>
      </div>

      {/* Header */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
              {claim.claim_reference}
            </h1>
          </div>
          <div style={{
            padding: '0.5rem 1rem',
            borderRadius: '12px',
            fontSize: '0.9rem',
            fontWeight: '600',
            background: claim.status_is_active ? '#e3f2fd' : '#f5f5f5',
            color: getStatusColor(claim.status_is_active)
          }}>
            {claim.status_is_active ? 'Active' : 'Closed'}
          </div>
        </div>

        <div style={{
          display: 'inline-block',
          padding: '0.75rem 1.25rem',
          background: '#f5f5f5',
          borderRadius: '6px',
          marginBottom: '1rem'
        }}>
          <strong>Current Status:</strong> {claim.client_status || claim.status}
        </div>

        {claim.status_description && (
          <div style={{
            padding: '1rem',
            background: '#e3f2fd',
            borderRadius: '6px',
            color: '#1565c0',
            marginBottom: '1rem'
          }}>
            <strong>Status Information:</strong>
            <p style={{ margin: '0.5rem 0 0 0' }}>{claim.status_description}</p>
          </div>
        )}

        {claim.faq_tag && (
          <div style={{ marginTop: '1rem' }}>
            <Link
              to={`/faq?tag=${claim.faq_tag}`}
              style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#4A90E2',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}
            >
              View Related FAQs
            </Link>
          </div>
        )}
      </div>

      {/* Claim Details */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginTop: '0', marginBottom: '1.5rem', color: '#333' }}>Claim Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <p style={{ margin: '0', fontSize: '0.85rem', color: '#666' }}>Submission Date</p>
            <p style={{ margin: '0.25rem 0 0 0', fontWeight: '600' }}>
              {isValidDate(claim.submission_date)
                ? new Date(claim.submission_date).toLocaleDateString()
                : 'Not submitted'}
            </p>
          </div>
          <div>
            <p style={{ margin: '0', fontSize: '0.85rem', color: '#666' }}>Last Updated</p>
            <p style={{ margin: '0.25rem 0 0 0', fontWeight: '600' }}>
              {new Date(claim.last_updated).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginTop: '0', marginBottom: '1.5rem', color: '#333' }}>Quick Actions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link
            to={`/claims/${claim.claim_reference}/communications`}
            style={{
              padding: '1rem',
              background: '#4A90E2',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              textAlign: 'center',
              fontWeight: '500'
            }}
          >
            View Communications
          </Link>
          <Link
            to={`/claims/${claim.claim_reference}/additional-info`}
            style={{
              padding: '1rem',
              background: '#4A90E2',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              textAlign: 'center',
              fontWeight: '500'
            }}
          >
            Submit Additional Info
          </Link>
          <Link
            to={`/claims/${claim.claim_reference}/attachments`}
            style={{
              padding: '1rem',
              background: '#4A90E2',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              textAlign: 'center',
              fontWeight: '500'
            }}
          >
            Upload Attachments
          </Link>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          onClick={() => navigate('/claims')}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'white',
            color: '#4A90E2',
            border: '2px solid #4A90E2',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Back to All Claims
        </button>
      </div>
    </Layout>
  );
};

export default ClaimDetail;
